/**
 * TournamentMatchScorer — Quick-Play-style tap-to-score UI used inside
 * the TournamentDetail "Record Score" dialog.
 *
 *  - Two big tappable columns (team1 / team2). Each tap adds +1.
 *  - Golden Point rule: first to `pointsPerGame` wins the game — at 20-20
 *    the very next point wins (no win-by-2).
 *  - Swap button flips the two columns visually (court-side change).
 *  - Live sync: every point is pushed to the backend (debounced) so
 *    spectators see it in real time and the score survives a closed dialog.
 *  - Shows Match # and Pool name so scorers confirm the right match.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Undo2, ArrowLeftRight, Zap } from 'lucide-react';

function detectWinner({ team1, team2, pointsPerGame }) {
  const need = Math.max(pointsPerGame, 1);
  if (team1 >= need && team1 > team2) return 1;
  if (team2 >= need && team2 > team1) return 2;
  return 0;
}

export default function TournamentMatchScorer({
  team1Name,
  team2Name,
  team1Id,
  team2Id,
  pointsPerGame = 21,
  bestOf = 1,
  onSubmit,
  onCancel,
  initialGames = [],
  matchNumber = null,
  poolLabel = null,
  onLiveSync = null,
}) {
  const buildSeed = () => {
    const base = initialGames.length > 0
      ? initialGames.map(g => ({ team1_score: g.team1_score || 0, team2_score: g.team2_score || 0 }))
      : [{ team1_score: 0, team2_score: 0 }];
    const winners = base.map(g => detectWinner({ team1: g.team1_score, team2: g.team2_score, pointsPerGame }));
    const w1 = winners.filter(w => w === 1).length;
    const w2 = winners.filter(w => w === 2).length;
    const needed = Math.ceil(bestOf / 2);
    if (winners[winners.length - 1] !== 0 && w1 < needed && w2 < needed && base.length < bestOf) {
      base.push({ team1_score: 0, team2_score: 0 });
    }
    return base;
  };
  const [games, setGames] = useState(buildSeed);
  const [currentIdx, setCurrentIdx] = useState(() => games.length - 1);
  // Stack of which team scored last, for Undo. Each entry: {gameIdx, team}.
  const [history, setHistory] = useState([]);
  const [swapped, setSwapped] = useState(false);

  // --- Live sync (debounced, fire-and-forget). Flushes on unmount so the
  // last few points are never lost when the dialog closes.
  const dirtyRef = useRef(false);
  const latestGamesRef = useRef(games);
  const syncTimerRef = useRef(null);
  const onLiveSyncRef = useRef(onLiveSync);
  useEffect(() => { onLiveSyncRef.current = onLiveSync; }, [onLiveSync]);
  useEffect(() => {
    latestGamesRef.current = games;
    if (!onLiveSyncRef.current || !dirtyRef.current) return;
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      onLiveSyncRef.current?.(latestGamesRef.current);
    }, 700);
  }, [games]);
  useEffect(() => () => {
    clearTimeout(syncTimerRef.current);
    if (dirtyRef.current) onLiveSyncRef.current?.(latestGamesRef.current);
  }, []);

  const winsNeeded = Math.ceil(bestOf / 2);

  const gameWinners = useMemo(
    () => games.map(g => detectWinner({ team1: g.team1_score, team2: g.team2_score, pointsPerGame })),
    [games, pointsPerGame]
  );

  const team1Wins = gameWinners.filter(w => w === 1).length;
  const team2Wins = gameWinners.filter(w => w === 2).length;
  const matchWinner = team1Wins >= winsNeeded ? 1 : team2Wins >= winsNeeded ? 2 : 0;
  const matchComplete = matchWinner !== 0;

  const ensureGameFor = (next) => {
    const curWinner = detectWinner({
      team1: next[currentIdx].team1_score,
      team2: next[currentIdx].team2_score,
      pointsPerGame,
    });
    if (curWinner !== 0) {
      const t1 = next.filter((_, i) => detectWinner({ team1: next[i].team1_score, team2: next[i].team2_score, pointsPerGame }) === 1).length;
      const t2 = next.filter((_, i) => detectWinner({ team1: next[i].team1_score, team2: next[i].team2_score, pointsPerGame }) === 2).length;
      if (t1 >= winsNeeded || t2 >= winsNeeded) return { next, advance: false };
      if (next.length < bestOf) {
        next.push({ team1_score: 0, team2_score: 0 });
        return { next, advance: true };
      }
    }
    return { next, advance: false };
  };

  const addPoint = (team) => {
    if (matchComplete) return;
    dirtyRef.current = true;
    setGames(prev => {
      const next = prev.map((g, i) => i === currentIdx
        ? { ...g, [team === 1 ? 'team1_score' : 'team2_score']: g[team === 1 ? 'team1_score' : 'team2_score'] + 1 }
        : g
      );
      const { next: maybe, advance } = ensureGameFor(next);
      if (advance) {
        setCurrentIdx(maybe.length - 1);
      }
      return maybe;
    });
    setHistory(h => [...h, { gameIdx: currentIdx, team }]);
    try { navigator.vibrate?.(15); } catch { /* ignore */ }
  };

  const undo = () => {
    if (history.length === 0) return;
    dirtyRef.current = true;
    const last = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setGames(prev => {
      let next = prev.map((g, i) => i === last.gameIdx
        ? { ...g, [last.team === 1 ? 'team1_score' : 'team2_score']: Math.max(0, g[last.team === 1 ? 'team1_score' : 'team2_score'] - 1) }
        : g
      );
      if (currentIdx > last.gameIdx && next[currentIdx]?.team1_score === 0 && next[currentIdx]?.team2_score === 0) {
        next = next.slice(0, currentIdx);
        setCurrentIdx(last.gameIdx);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!matchComplete) {
      if (!window.confirm('Match is still in progress. Save the current scores anyway?')) return;
    }
    const winnerId = matchWinner === 1 ? team1Id : matchWinner === 2 ? team2Id : null;
    onSubmit({ games, winner_id: winnerId });
  };

  const currentGame = games[currentIdx] || { team1_score: 0, team2_score: 0 };
  const goldenPoint = !matchComplete
    && currentGame.team1_score === pointsPerGame - 1
    && currentGame.team2_score === pointsPerGame - 1;

  // Column definitions honouring the visual swap.
  const columns = [
    { team: 1, name: team1Name, score: currentGame.team1_score, tone: 'primary' },
    { team: 2, name: team2Name, score: currentGame.team2_score, tone: 'secondary' },
  ];
  const ordered = swapped ? [columns[1], columns[0]] : columns;

  return (
    <div className="space-y-4" data-testid="tournament-match-scorer">
      {/* Match context — so the scorer confirms they're on the right match */}
      {(matchNumber || poolLabel) && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="scorer-match-info">
          {matchNumber && (
            <Badge className="bg-primary/15 text-primary border-primary/30 font-mono text-[11px]" data-testid="scorer-match-number">
              Match #{matchNumber}
            </Badge>
          )}
          {poolLabel && (
            <Badge variant="outline" className="text-[11px]" data-testid="scorer-pool-label">{poolLabel}</Badge>
          )}
        </div>
      )}

      {/* Game tabs */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          {Array.from({ length: bestOf }).map((_, i) => {
            const w = gameWinners[i] ?? 0;
            const isCurrent = i === currentIdx;
            const exists = i < games.length;
            return (
              <button
                key={`g${i}`}
                type="button"
                disabled={!exists}
                onClick={() => exists && setCurrentIdx(i)}
                className={`px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border-2 transition-colors ${
                  isCurrent
                    ? 'border-primary bg-primary/15 text-primary'
                    : exists
                      ? 'border-border bg-muted/30 text-foreground/70 hover:bg-muted'
                      : 'border-dashed border-border/50 text-muted-foreground/50'
                }`}
                data-testid={`game-tab-${i}`}
              >
                G{i + 1}
                {exists && w !== 0 && (
                  <span className="ml-1 text-[9px]">{w === 1 ? '✓1' : '✓2'}</span>
                )}
              </button>
            );
          })}
        </div>
        <Badge variant="outline" className="text-[10px] font-mono">
          BO{bestOf} · to {pointsPerGame} · golden pt
        </Badge>
      </div>

      {/* Golden point alert */}
      {goldenPoint && (
        <div className="flex items-center justify-center gap-2 rounded-md border-2 border-amber-500/60 bg-amber-500/10 px-3 py-2 animate-pulse" data-testid="golden-point-banner">
          <Zap className="h-4 w-4 text-amber-500" />
          <span className="text-xs font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
            Golden Point — next point wins the game!
          </span>
        </div>
      )}

      {/* Score columns — Quick Play style, with centered swap button */}
      <div className="relative">
        <div className="grid grid-cols-2 gap-2 rounded-xl border-2 border-border overflow-hidden bg-card">
          {ordered.map((col, i) => (
            <button
              key={`col-${col.team}`}
              type="button"
              onClick={() => addPoint(col.team)}
              disabled={matchComplete}
              className={`flex flex-col items-center justify-center py-6 px-2 disabled:opacity-50 transition-colors ${i === 0 ? 'border-r border-border' : ''} ${col.tone === 'primary' ? 'hover:bg-primary/10 active:bg-primary/20' : 'hover:bg-secondary/10 active:bg-secondary/20'}`}
              data-testid={`tap-team${col.team}`}
            >
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground truncate max-w-full" title={col.name}>
                {col.name}
              </p>
              <p className={`font-heading text-6xl font-black tabular-nums mt-2 ${col.tone === 'primary' ? 'text-primary' : 'text-secondary'}`} data-testid={`score-team${col.team}`}>
                {col.score}
              </p>
              {!matchComplete && (
                <span className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                  <Plus className="h-3 w-3" /> Tap to score
                </span>
              )}
            </button>
          ))}
        </div>
        {/* Swap sides */}
        <button
          type="button"
          onClick={() => setSwapped(s => !s)}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full border-2 border-border bg-background shadow-md flex items-center justify-center hover:bg-muted transition-colors"
          title="Swap player positions (court sides)"
          aria-label="Swap player positions"
          data-testid="scorer-swap-btn"
        >
          <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Match status */}
      <div className="text-center text-xs">
        {matchComplete ? (
          <p className="font-bold text-emerald-600 dark:text-emerald-400" data-testid="match-winner-banner">
            🏆 {matchWinner === 1 ? team1Name : team2Name} wins ({team1Wins}-{team2Wins})
          </p>
        ) : (
          <p className="text-muted-foreground">
            Games — {team1Name}: {team1Wins} · {team2Name}: {team2Wins} (first to {winsNeeded})
          </p>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={undo}
          disabled={history.length === 0}
          data-testid="scorer-undo-btn"
        >
          <Undo2 className="h-3.5 w-3.5 mr-1" />
          Undo
        </Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="scorer-cancel-btn">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-secondary hover:bg-secondary/90"
            onClick={handleSave}
            data-testid="scorer-save-btn"
          >
            Save Score
          </Button>
        </div>
      </div>
      {onLiveSync && (
        <p className="text-[10px] text-center text-muted-foreground" data-testid="autosave-hint">
          Score auto-saves as you tap — closing this window keeps the score. Spectators see it live.
        </p>
      )}
    </div>
  );
}
